import * as z from 'zod/mini';

import type { QuickFilter } from '../model/filter';
import type { StorageLike } from './storage';
import { readItem, writeItem } from './storage';

export const SETTINGS_KEY = 'kinklist:settings:v1';

export type ThemePreference = 'system' | 'light' | 'dark';
export type { QuickFilter };

/** Device-local preferences. Never part of shared payloads. */
export interface Settings {
  theme: ThemePreference;
  /** Embed re-importable data in exported images. */
  embedData: boolean;
  /** Draw comments as footnotes in exported images. */
  imageFootnotes: boolean;
  filter: QuickFilter;
  hintDismissed: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  embedData: true,
  imageFootnotes: false,
  filter: 'all',
  hintDismissed: false,
};

// Each field falls back to its default on its own, so one bad value doesn't reset the rest.
const settingsSchema = z.object({
  theme: z.catch(z.enum(['system', 'light', 'dark']), DEFAULT_SETTINGS.theme),
  embedData: z.catch(z.boolean(), DEFAULT_SETTINGS.embedData),
  imageFootnotes: z.catch(z.boolean(), DEFAULT_SETTINGS.imageFootnotes),
  filter: z.catch(z.enum(['all', 'unrated', 'commented']), DEFAULT_SETTINGS.filter),
  hintDismissed: z.catch(z.boolean(), DEFAULT_SETTINGS.hintDismissed),
}) satisfies z.ZodMiniType<Settings>;

export function parseSettings(raw: string | null): Settings {
  if (raw === null) return { ...DEFAULT_SETTINGS };
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
  const result = settingsSchema.safeParse(typeof data === 'object' && data !== null ? data : {});
  return result.success ? result.data : { ...DEFAULT_SETTINGS };
}

/** Reactive settings, persisted on every change. */
export class SettingsStore {
  #value: Settings;
  readonly #storage: StorageLike | null;

  constructor(storage: StorageLike | null) {
    this.#storage = storage;
    this.#value = $state.raw(parseSettings(readItem(storage, SETTINGS_KEY)));
  }

  get value(): Settings {
    return this.#value;
  }

  update(patch: Partial<Settings>): void {
    this.#value = { ...this.#value, ...patch };
    writeItem(this.#storage, SETTINGS_KEY, JSON.stringify(this.#value));
  }
}
